/**
 * Google AdSense — Auto ads + native in-feed units.
 *
 * Ads are consent-gated: the script is only injected once the visitor accepts
 * cookies in the banner (same choice that unlocks Consent Mode ad storage).
 * Without consent nothing is loaded and no ad slot renders.
 */

export const ADSENSE_CLIENT = "ca-pub-8251395968499153";

/**
 * In-feed slot ids created in AdSense (Ads > By ad unit > In-feed).
 * Each unit also has a layout key (`data-ad-layout-key`, looks like
 * "-fb+5w+4e-db+86") that AdSense gives you next to the slot id.
 *
 * They can be set here, or overridden per environment with
 * VITE_ADSENSE_BOARD_FEED / VITE_ADSENSE_BOARD_FEED_KEY (same for HOT_FEED).
 * An empty id renders nothing, so the boards stay clean until it is filled in.
 */
type SlotConfig = { id: string; layoutKey?: string };

const env = import.meta.env as Record<string, string | undefined>;

export const AD_SLOTS: Record<"boardFeed" | "hotFeed", SlotConfig> = {
  /** Native in-feed unit shown between board rows on / and /c/:slug. */
  boardFeed: {
    id: env["VITE_ADSENSE_BOARD_FEED"] ?? "",
    layoutKey: env["VITE_ADSENSE_BOARD_FEED_KEY"] ?? "",
  },
  /** Native in-feed unit shown between the category cards on /hot. */
  hotFeed: {
    id: env["VITE_ADSENSE_HOT_FEED"] ?? "",
    layoutKey: env["VITE_ADSENSE_HOT_FEED_KEY"] ?? "",
  },
};

export type AdSlotName = keyof typeof AD_SLOTS;


declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let loading = false;

/** Inject the AdSense tag once. Auto ads start themselves from this script. */
export function loadAdSense() {
  if (typeof window === "undefined" || loading) return;
  if (document.querySelector('script[data-behot-adsense="1"]')) {
    loading = true;
    return;
  }
  loading = true;
  const script = document.createElement("script");
  script.async = true;
  script.crossOrigin = "anonymous";
  script.dataset["behotAdsense"] = "1";
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  document.head.appendChild(script);
}

export function isAdSenseLoaded() {
  return loading;
}

/** Ask AdSense to fill a freshly mounted <ins> element. */
export function pushAd() {
  if (typeof window === "undefined") return;
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch {
    /* blocked by an ad blocker — the slot stays empty */
  }
}
